// ---------------------------------------------------------------------------
// UI Slice — editor chrome state (breakpoint, panels, zoom, pan, preview)
// ---------------------------------------------------------------------------

import type { AppTarget, Breakpoint, RailItem } from './types'

export type ColorScheme = 'light' | 'dark'

export interface UiSlice {
  activeBreakpoint: Breakpoint
  appTarget: AppTarget
  isPreview: boolean
  leftPanelOpen: boolean
  rightPanelOpen: boolean
  activeRail: RailItem
  zoom: number
  panX: number
  panY: number
  hoveredElementId: string | null
  /** Color scheme for the app preview (light or dark) */
  previewColorScheme: ColorScheme

  setBreakpoint: (bp: Breakpoint) => void
  setAppTarget: (target: AppTarget) => void
  togglePreview: () => void
  toggleLeftPanel: () => void
  toggleRightPanel: () => void
  setActiveRail: (rail: RailItem) => void
  setZoom: (zoom: number) => void
  setPan: (x: number, y: number) => void
  setHoveredElement: (id: string | null) => void
  setPreviewColorScheme: (scheme: ColorScheme) => void
}

type Set = (fn: (state: UiSlice) => void) => void

export function createUiSlice(
  set: Set,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _get: unknown,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _api: unknown,
): UiSlice {
  return {
    activeBreakpoint: 'desktop',
    appTarget: 'web',
    isPreview: false,
    leftPanelOpen: true,
    rightPanelOpen: true,
    activeRail: 'layers',
    zoom: 1,
    panX: 0,
    panY: 0,
    hoveredElementId: null,
    previewColorScheme: 'light',

    setBreakpoint: (bp) =>
      set((state) => {
        state.activeBreakpoint = bp
      }),

    setAppTarget: (target) =>
      set((state) => {
        state.appTarget = target
      }),

    togglePreview: () =>
      set((state) => {
        state.isPreview = !state.isPreview
      }),

    toggleLeftPanel: () =>
      set((state) => {
        state.leftPanelOpen = !state.leftPanelOpen
      }),

    toggleRightPanel: () =>
      set((state) => {
        state.rightPanelOpen = !state.rightPanelOpen
      }),

    setActiveRail: (rail) =>
      set((state) => {
        // Left panel is always visible — just switch the active rail
        state.activeRail = rail
        state.leftPanelOpen = true
      }),

    setZoom: (zoom) =>
      set((state) => {
        state.zoom = zoom
      }),

    setPan: (x, y) =>
      set((state) => {
        state.panX = x
        state.panY = y
      }),

    setHoveredElement: (id) =>
      set((state) => {
        state.hoveredElementId = id
      }),

    setPreviewColorScheme: (scheme) =>
      set((state) => {
        state.previewColorScheme = scheme
      }),
  }
}
